import { useEffect, useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { friendlyError } from "@/lib/friendlyError";

type Memory = {
  id: string;
  statement: string;
  category: string;
  confidence: string;
  source: string;
  source_note: string | null;
  related_records: any;
  tags: string[];
  effective_start: string | null;
  effective_end: string | null;
  status: string;
  retired_reason: string | null;
  created_at: string;
  updated_at: string;
};

type Article = {
  id: string;
  title: string;
  body: string;
  category: string;
  tags: string[];
  effective_start: string | null;
  effective_end: string | null;
  status: string;
  source_note: string | null;
  created_at: string;
  updated_at: string;
};

function fmtDate(d: string | null) {
  return d ? new Date(d).toLocaleDateString() : "—";
}

export default function KnowledgeVaultPage() {
  const [memories, setMemories] = useState<Memory[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [query, setQuery] = useState("");
  const [showRetired, setShowRetired] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [m, a] = await Promise.all([
      supabase
        .from("eos_memories" as any)
        .select("*")
        .order("updated_at", { ascending: false })
        .limit(500),
      supabase
        .from("eos_knowledge_articles" as any)
        .select("*")
        .order("updated_at", { ascending: false })
        .limit(200),
    ]);
    if (m.error) friendlyError({ what: "I couldn't open the memory library.", nextStep: "Try refreshing the page.", technical: m.error });
    else setMemories((m.data ?? []) as unknown as Memory[]);
    if (a.error) friendlyError({ what: "I couldn't open the knowledge articles.", nextStep: "Try refreshing the page.", technical: a.error });
    else setArticles((a.data ?? []) as unknown as Article[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const retireMemory = async (id: string) => {
    const reason = window.prompt("Reason for retiring this memory?");
    if (!reason) return;
    const { error } = await supabase
      .from("eos_memories" as any)
      .update({ status: "retired", retired_at: new Date().toISOString(), retired_reason: reason } as any)
      .eq("id", id);
    if (error) friendlyError({ what: "I couldn't retire that memory.", technical: error });
    else load();
  };
  const restoreMemory = async (id: string) => {
    const { error } = await supabase
      .from("eos_memories" as any)
      .update({ status: "active", retired_at: null, retired_reason: null } as any)
      .eq("id", id);
    if (error) friendlyError({ what: "I couldn't restore that memory.", technical: error });
    else load();
  };
  const retireArticle = async (id: string) => {
    const reason = window.prompt("Reason for retiring this article?");
    if (!reason) return;
    const { error } = await supabase
      .from("eos_knowledge_articles" as any)
      .update({ status: "retired", retired_at: new Date().toISOString(), retired_reason: reason } as any)
      .eq("id", id);
    if (error) friendlyError({ what: "I couldn't retire that article.", technical: error });
    else load();
  };
  const restoreArticle = async (id: string) => {
    const { error } = await supabase
      .from("eos_knowledge_articles" as any)
      .update({ status: "active", retired_at: null, retired_reason: null } as any)
      .eq("id", id);
    if (error) friendlyError({ what: "I couldn't restore that article.", technical: error });
    else load();
  };

  const q = query.trim().toLowerCase();
  const filteredMemories = memories.filter((m) => {
    if (!showRetired && m.status !== "active") return false;
    if (!q) return true;
    return (
      m.statement.toLowerCase().includes(q) ||
      m.category.toLowerCase().includes(q) ||
      JSON.stringify(m.related_records ?? []).toLowerCase().includes(q) ||
      (m.tags ?? []).some((t) => t.toLowerCase().includes(q))
    );
  });
  const filteredArticles = articles.filter((a) => {
    if (!showRetired && a.status !== "active") return false;
    if (!q) return true;
    return (
      a.title.toLowerCase().includes(q) ||
      a.body.toLowerCase().includes(q) ||
      a.category.toLowerCase().includes(q) ||
      (a.tags ?? []).some((t) => t.toLowerCase().includes(q))
    );
  });

  return (
    <AdminShell section="knowledge_vault">
      <div className="container mx-auto p-4 md:p-6 space-y-4 max-w-5xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Knowledge Vault</h1>
            <p className="text-sm text-muted-foreground">
              Executive Memory (durable facts) and Knowledge Vault (policies, procedures, agreements).
              Ask MAVIS to add, update, or retire entries — everything routes through a Proposed Change card.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Refresh
          </Button>
        </div>

        <Card>
          <CardContent className="p-3 flex flex-wrap items-center gap-3">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search statements, titles, tags, linked people…"
              className="flex-1 min-w-[240px]"
            />
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
              Include retired
            </label>
          </CardContent>
        </Card>

        <Tabs defaultValue="memories">
          <TabsList>
            <TabsTrigger value="memories">
              Memories <Badge variant="secondary" className="ml-2">{filteredMemories.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="articles">
              Articles <Badge variant="secondary" className="ml-2">{filteredArticles.length}</Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="memories" className="space-y-2 mt-4">
            {!loading && filteredMemories.length === 0 && (
              <div className="text-sm text-muted-foreground">
                No memories yet. Tell MAVIS "Remember that…" to add one.
              </div>
            )}
            {filteredMemories.map((m) => (
              <Card key={m.id} className={m.status !== "active" ? "opacity-60" : ""}>
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <Badge variant="outline">{m.category.replace(/_/g, " ")}</Badge>
                        {m.confidence !== "high" && (
                          <Badge variant="secondary">confidence: {m.confidence}</Badge>
                        )}
                        {m.status !== "active" && <Badge variant="destructive">{m.status}</Badge>}
                        <span className="text-xs text-muted-foreground">
                          Effective {fmtDate(m.effective_start)} → {fmtDate(m.effective_end) || "ongoing"}
                        </span>
                      </div>
                      <div className="text-sm font-medium">{m.statement}</div>
                      {Array.isArray(m.related_records) && m.related_records.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Linked:{" "}
                          {m.related_records
                            .map((r: any) => r.label || r.person_name || `${r.table ?? ""}:${r.id ?? ""}`)
                            .join(", ")}
                        </div>
                      )}
                      {m.source_note && (
                        <div className="text-xs text-muted-foreground italic mt-1">Source: {m.source_note}</div>
                      )}
                      {m.retired_reason && (
                        <div className="text-xs text-destructive mt-1">Retired: {m.retired_reason}</div>
                      )}
                    </div>
                    {m.status === "active" ? (
                      <Button size="sm" variant="ghost" onClick={() => retireMemory(m.id)} title="Retire">
                        <Archive className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => restoreMemory(m.id)} title="Restore">
                        <ArchiveRestore className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="articles" className="space-y-2 mt-4">
            {!loading && filteredArticles.length === 0 && (
              <div className="text-sm text-muted-foreground">
                No articles yet. Tell MAVIS "Save a policy for…" to add one.
              </div>
            )}
            {filteredArticles.map((a) => (
              <Card key={a.id} className={a.status !== "active" ? "opacity-60" : ""}>
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <Badge variant="outline">{a.category.replace(/_/g, " ")}</Badge>
                        {a.status !== "active" && <Badge variant="destructive">{a.status}</Badge>}
                        {a.effective_end && (
                          <span className="text-xs text-muted-foreground">
                            Effective through {fmtDate(a.effective_end)}
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-semibold">{a.title}</div>
                      <div className="text-sm text-muted-foreground whitespace-pre-wrap mt-1">{a.body}</div>
                      {Array.isArray(a.tags) && a.tags.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-1">Tags: {a.tags.join(", ")}</div>
                      )}
                      {a.source_note && (
                        <div className="text-xs text-muted-foreground italic mt-1">Source: {a.source_note}</div>
                      )}
                    </div>
                    {a.status === "active" ? (
                      <Button size="sm" variant="ghost" onClick={() => retireArticle(a.id)} title="Retire">
                        <Archive className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => restoreArticle(a.id)} title="Restore">
                        <ArchiveRestore className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </AdminShell>
  );
}
